import React, { useState, useEffect, useMemo } from 'react'
import { Outlet, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { GlobalSidebar } from './GlobalSidebar'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { useSessionStorage } from '../../hooks/useSessionStorage'
import { isJiraConnect, getAllContext, fetchJiraProjects } from '../../utils/jiraContext'
import { JIRA_PROJECT_ID, setActiveProjectId } from '../../constants/config'
import { useLicense } from '../../contexts/LicenseContext'
import LicenseRequiredPage from '../common/LicenseRequiredPage'

/**
 * APP LAYOUT COMPONENT
 *
 * Main layout wrapper for the application.
 * Contains the global sidebar and renders page content via React Router Outlet.
 *
 * URL-as-source-of-truth design for selected project:
 * - The hash route's ?project=<key> is the authoritative selection.
 * - selectedProject is DERIVED from URL + projects list, not independent state.
 * - Fallback chain when URL lacks the param: sessionStorage key → Jira context
 *   key → first project in list.
 * - handleProjectChange just navigates; derived selection follows atomically.
 * - No reconciliation loop possible — there is nothing to reconcile.
 */
function AppLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  // Sidebar collapse state - persisted to localStorage
  const [isCollapsed, setIsCollapsed] = useLocalStorage('sidebar-collapsed', false)

  // Project list from API (populates the sidebar selector)
  const [projects, setProjects] = useState([])
  const [isLoadingProjects, setIsLoadingProjects] = useState(true)

  // Fallback-key stores (used only when URL lacks ?project=)
  // sessionStorage persists the last selected key per-tab across refreshes.
  // (Old 'selected-project' key that held a full object is orphaned and ignored.)
  const [sessionProjectKey, setSessionProjectKey] = useSessionStorage('selected-project-key', null)
  // Jira's current project context — set once on mount as the final fallback.
  const [jiraContextKey, setJiraContextKey] = useState(null)

  // URL hash search param is the authoritative selection
  const urlKey = searchParams.get('project')

  // Derive the currently selected project. Pure function of inputs — no race possible.
  const selectedProject = useMemo(() => {
    if (projects.length === 0) return null

    const find = (k) => {
      if (!k) return null
      return projects.find(p => p.key === k || String(p.value) === String(k)) || null
    }

    if (urlKey) {
      const hit = find(urlKey)
      if (hit) return hit
      console.warn(`[AppLayout] URL project "${urlKey}" not in accessible list — falling back`)
    }
    return find(sessionProjectKey) || find(jiraContextKey) || projects[0] || null
  }, [projects, urlKey, sessionProjectKey, jiraContextKey])

  // Load projects + Jira context on mount (populates inputs for the derivation)
  useEffect(() => {
    const initialize = async () => {
      setIsLoadingProjects(true)
      try {
        if (isJiraConnect()) {
          const jiraProjects = await fetchJiraProjects()
          const projectOptions = jiraProjects.map(project => ({
            value: project.id,
            label: `${project.name} (${project.key})`,
            name: project.name,
            key: project.key,
            avatarUrl: project.avatarUrl
          }))
          setProjects(projectOptions)

          // Capture Jira's current project context for the fallback chain
          const context = await getAllContext()
          const ctxProject = projectOptions.find(p =>
            p.value === context.projectId || String(p.value) === String(context.projectId)
          )
          if (ctxProject) setJiraContextKey(ctxProject.key)
        } else {
          // Standalone/dev mode — mock projects
          const projectId = JIRA_PROJECT_ID
          const mockProjects = [
            { value: projectId, label: `Demo Project (${projectId})`, name: 'Demo Project', key: 'DEMO' },
            { value: 10002, label: 'Test Project 2', name: 'Test Project 2', key: 'TP2' },
            { value: 10003, label: 'Test Project 3', name: 'Test Project 3', key: 'TP3' }
          ]
          setProjects(mockProjects)
          setJiraContextKey('DEMO')
        }
      } catch (error) {
        console.warn('Failed to initialize projects:', error)
        const projectId = JIRA_PROJECT_ID
        setProjects([{
          value: projectId,
          label: `Demo Project (${projectId})`,
          name: 'Demo Project',
          key: 'DEMO'
        }])
        setJiraContextKey('DEMO')
      } finally {
        setIsLoadingProjects(false)
      }
    }
    initialize()
  }, [])

  // Keep the activeProjectId module var aligned with the derived selection.
  // API helpers read this as a fallback when a project id isn't passed explicitly.
  useEffect(() => {
    if (selectedProject?.value) {
      setActiveProjectId(selectedProject.value)
    }
  }, [selectedProject?.value])

  // Persist the derived key to sessionStorage so the next page load (without
  // ?project= in URL) can restore the user's last selection for this tab.
  useEffect(() => {
    if (selectedProject?.key && selectedProject.key !== sessionProjectKey) {
      setSessionProjectKey(selectedProject.key)
    }
  }, [selectedProject?.key])

  // Write-back to URL when it lacks ?project= (e.g., navigate('/library/scenario/252')
  // from Library drops the search string). This keeps every in-app URL self-describing
  // for sharing. Skips standalone panel routes.
  useEffect(() => {
    if (!selectedProject?.key) return
    if (location.pathname === '/issue-panel' || location.pathname === '/link-scenarios-dialog') return
    if (urlKey === selectedProject.key) return

    const next = new URLSearchParams(searchParams)
    next.set('project', selectedProject.key)
    setSearchParams(next, { replace: true })
  }, [selectedProject?.key, urlKey, location.pathname])

  // Handle project change — just navigate. Derived selectedProject updates atomically.
  const handleProjectChange = (newProject) => {
    if (!newProject?.key) return
    navigate(`/releases?project=${newProject.key}`)
  }

  // License validation gate
  const { isLicenseValid, licenseMessage } = useLicense()

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed)
  }

  if (!isLicenseValid) {
    return <LicenseRequiredPage message={licenseMessage} />
  }

  return (
    <div
      className="app-layout"
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: 'var(--ds-surface-sunken, #F4F5F7)'
      }}
    >
      {/* Global Navigation Sidebar */}
      <GlobalSidebar
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
        selectedProject={selectedProject}
        projects={projects}
        onProjectChange={handleProjectChange}
      />

      {/* Main Content Area */}
      <div
        className="app-content"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Page content rendered by React Router */}
        <Outlet context={{ selectedProject, isLoadingProjects }} />
      </div>
    </div>
  )
}

export { AppLayout }
